import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "@/components/PhotoViewer/PhotoViewer.scss";

interface PhotoViewerProps {
  title: string;
  photos: string[];
  /** Index of the photo shown large, or null to show the grid of all photos. */
  activeIndex: number | null;
  onActiveIndexChange: (index: number | null) => void;
  onClose: () => void;
}

/**
 * Full-screen photo browser: a grid of every photo, and a single-photo view with
 * previous/next. Keyboard: Esc closes (or goes back to the grid), ← → browse.
 */
function PhotoViewer({
  title,
  photos,
  activeIndex,
  onActiveIndexChange,
  onClose,
}: PhotoViewerProps) {
  const { t } = useTranslation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const total = photos.length;

  // Lock the page behind the viewer and move focus into it.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (activeIndex !== null) {
          onActiveIndexChange(null);
        } else {
          onClose();
        }
      } else if (activeIndex !== null && event.key === "ArrowRight") {
        onActiveIndexChange((activeIndex + 1) % total);
      } else if (activeIndex !== null && event.key === "ArrowLeft") {
        onActiveIndexChange((activeIndex - 1 + total) % total);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, total, onActiveIndexChange, onClose]);

  const photoAlt = (index: number) =>
    t("propertyDetails.photoAlt", { index: index + 1 });

  return (
    <div
      className={`photo-viewer${
        activeIndex !== null ? " photo-viewer--single" : ""
      }`}
      role="dialog"
      aria-modal="true"
      aria-label={t("photoViewer.label", { title })}
    >
      <div className="photo-viewer__bar">
        {activeIndex !== null ? (
          <button
            type="button"
            className="photo-viewer__text-button"
            onClick={() => onActiveIndexChange(null)}
          >
            ← {t("photoViewer.allPhotos")}
          </button>
        ) : (
          <span className="photo-viewer__title">{title}</span>
        )}

        {activeIndex !== null && (
          <span className="photo-viewer__counter" aria-live="polite">
            {t("photoViewer.counter", { current: activeIndex + 1, total })}
          </span>
        )}

        <button
          ref={closeButtonRef}
          type="button"
          className="photo-viewer__text-button"
          onClick={onClose}
        >
          {t("photoViewer.close")} ✕
        </button>
      </div>

      {activeIndex === null ? (
        <div className="photo-viewer__scroll">
          <div className="photo-viewer__grid">
            {photos.map((url, index) => (
              <button
                key={url}
                type="button"
                className="photo-viewer__tile"
                onClick={() => onActiveIndexChange(index)}
              >
                <img src={url} alt={photoAlt(index)} loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="photo-viewer__stage">
          {total > 1 && (
            <button
              type="button"
              className="photo-viewer__nav photo-viewer__nav--previous"
              aria-label={t("photoViewer.previous")}
              onClick={() =>
                onActiveIndexChange((activeIndex - 1 + total) % total)
              }
            >
              ‹
            </button>
          )}
          <img
            className="photo-viewer__photo"
            src={photos[activeIndex]}
            alt={photoAlt(activeIndex)}
          />
          {total > 1 && (
            <button
              type="button"
              className="photo-viewer__nav photo-viewer__nav--next"
              aria-label={t("photoViewer.next")}
              onClick={() => onActiveIndexChange((activeIndex + 1) % total)}
            >
              ›
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default PhotoViewer;
export type { PhotoViewerProps };
